/**
 * Order Builder Module
 * Handles order payload construction and line item creation
 * Extracted from csvImportApiClient.js for better maintainability
 */

// import PriceCalculator from '../pricing/PriceCalculator';

// Debug logging
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class OrderBuilder {
    constructor(baseApiClient, priceCalculator = null, countryShipping = null) {
        this.baseClient = baseApiClient;
        this.api = baseApiClient.api; // Keep for backward compatibility
        this.priceCalculator = priceCalculator;
        this.countryShipping = countryShipping;
    }

    /**
     * Parse order date from various formats
     * @param {string} dateValue - Date value from CSV
     * @returns {Date|null} Parsed date or null if invalid
     */
    parseOrderDate(dateValue) {
        if (!dateValue) return null;
        
        try {
            // Common date formats
            const dateFormats = [
                // ISO formats
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // 2024-01-15T10:30:00
                /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/, // 2024-01-15 10:30:00
                /^\d{4}-\d{2}-\d{2}/, // 2024-01-15
                // German formats
                /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2}:\d{2}/, // 15.01.2024 10:30:00
                /^\d{2}\.\d{2}\.\d{4}/, // 15.01.2024
                // US formats
                /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}/, // 01/15/2024 10:30:00
                /^\d{2}\/\d{2}\/\d{4}/ // 01/15/2024
            ];
            
            // Try direct parsing first
            let parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
                return parsedDate;
            }
            
            // Try format-specific parsing
            for (const format of dateFormats) {
                if (format.test(dateValue)) {
                    if (dateValue.includes('.')) {
                        // German format - convert to ISO
                        const parts = dateValue.split(/[.\s:]/);
                        if (parts.length >= 3) {
                            const day = parts[0];
                            const month = parts[1];
                            const year = parts[2];
                            const hour = parts[3] || '00';
                            const minute = parts[4] || '00';
                            const second = parts[5] || '00';
                            
                            parsedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
                            if (!isNaN(parsedDate.getTime())) {
                                return parsedDate;
                            }
                        }
                    }
                    break;
                }
            }
            
            return null;
        } catch (error) {
            console.warn('[Order Builder] Error parsing date:', dateValue, error);
            return null;
        }
    }

    /**
     * Set nested value in object using dot notation
     * @param {Object} obj - Target object
     * @param {string} path - Dot notation path
     * @param {*} value - Value to set
     */
    setNestedValue(obj, path, value) {
        const keys = path.split('.');
        let current = obj;
        
        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!(key in current) || typeof current[key] !== 'object' || current[key] === null) {
                current[key] = {};
            }
            current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
    }

    /**
     * Build order payload from CSV data
     * @param {Object} params - Build parameters
     * @returns {Object} Complete order payload
     */
    async buildOrderPayload(params) {
        const {
            csvData,
            customer,
            billingAddress,
            shippingAddress,
            profile,
            salesChannelId,
            orderType = 'single',
            orderIndex = 1,
            csvOrderNumber = null
        } = params;

        let orderDate = new Date();
        
        // Get order number and order date from CSV data using field mapping
        let orderNumber = csvOrderNumber || `CSV-${orderDate.getTime()}${orderType === 'multiple' ? '-' + orderIndex : ''}`;
        
        if (profile.fieldMapping && csvData.data && csvData.data.length > 0) {
            const firstRow = csvData.data[0];
            
            // Look for order number mapping in profile (unless already provided as csvOrderNumber)
            if (!csvOrderNumber) {
                const orderNumberMappingKey = Object.keys(profile.fieldMapping).find(key => 
                    profile.fieldMapping[key] === 'orderNumber' || 
                    profile.fieldMapping[key] === 'orderDateTime' ||
                    key.toLowerCase().includes('ordernumber') ||
                    key.toLowerCase().includes('order_number')
                );
                if (orderNumberMappingKey && firstRow[orderNumberMappingKey]) {
                    orderNumber = firstRow[orderNumberMappingKey].toString().replace(/[/\\]/g, '-');
                }
            }
            
            // Look for order date/time mapping in profile
            const orderDateMappingKey = Object.keys(profile.fieldMapping).find(key => 
                profile.fieldMapping[key] === 'orderDateTime' || 
                profile.fieldMapping[key] === 'orderDate' ||
                key.toLowerCase().includes('orderdate') ||
                key.toLowerCase().includes('order_date') ||
                key.toLowerCase().includes('date')
            );
            
            if (orderDateMappingKey && firstRow[orderDateMappingKey]) {
                const parsedDate = this.parseOrderDate(firstRow[orderDateMappingKey]);
                if (parsedDate) {
                    orderDate = parsedDate;
                }
            }
        }
        
        if (DEBUG_API) {
            console.log(`[Order Builder] Building order payload for: ${orderNumber}`);
            console.log(`[Order Builder] Order date: ${orderDate.toISOString()}`);
        }
        
        // Calculate tax and pricing BEFORE building line items (so taxRate is available)
        const country = this.countryShippingUtils
            ? this.countryShippingUtils.extractCountryFromRow(
                (csvData.data && csvData.data.length > 0) ? csvData.data[0] : {}, profile)
            : ((csvData.data && csvData.data.length > 0) ? (csvData.data[0]['Country'] || 'DE') : 'DE');
        const taxShippingConfig = await this.getTaxShippingConfig(country, profile);
        const taxRate = taxShippingConfig.taxRate;

        // Build line items from CSV data (with correct country-specific tax rate)
        const { lineItems, totalPrice, fallbackPriceProducts } = await this.buildLineItems(csvData, profile, customer, taxRate, taxStatus);

        // Get shipping method configuration
        const shippingConfig = await this.getShippingConfiguration(shippingAddress, profile);
        
        // Determine tax status: "tax-free", "net", or "gross"
        const taxStatus = this.determineTaxStatus(customer, profile);
        const isTaxFree = taxStatus === 'tax-free';
        const isGross = taxStatus === 'gross';

        // Generate UUID for billing address (without dashes for Shopware compatibility)
        const billingAddressId = this.generateUUID().replace(/-/g, '');
        console.log('[Order Builder] Generated billing address ID:', billingAddressId);

        // Get delivery date from CSV or use default
        const deliveryDate = this.getDeliveryDate(csvData, profile);

        // Build custom fields
        const customFields = this.buildCustomFields(orderNumber, deliveryDate);

        // Calculate pricing
        // totalPrice from lineItems is NET (when taxStatus=net) or GROSS (when taxStatus=gross)
        const itemTotal = parseFloat(totalPrice.toFixed(2));
        let netPrice, grossPrice, totalTaxPrice;

        if (isTaxFree) {
            netPrice = itemTotal;
            grossPrice = itemTotal;
            totalTaxPrice = 0;
        } else if (isGross) {
            // totalPrice from lineItems is GROSS — extract net from it
            grossPrice = itemTotal;
            totalTaxPrice = parseFloat((grossPrice - (grossPrice / (1 + taxRate / 100))).toFixed(2));
            netPrice = parseFloat((grossPrice - totalTaxPrice).toFixed(2));
        } else {
            // net: totalPrice is NET — calculate gross
            netPrice = itemTotal;
            totalTaxPrice = parseFloat((netPrice * (taxRate / 100)).toFixed(2));
            grossPrice = parseFloat((netPrice + totalTaxPrice).toFixed(2));
        }

        // Build main order payload
        const orderPayload = {
            customFields: customFields,
            billingAddressId: billingAddressId,
            currencyId: "b7d2554b0ce847cd82f3ac9bd1c0dfca", // Standard currency ID
            languageId: customer.languageId,
            salesChannelId: salesChannelId,
            stateId: "018ad12f18ef705a9b355e2d9224b11f", // Standard state ID
            orderDateTime: orderDate.toISOString().replace('T', ' ').substring(0, 19), // "YYYY-MM-DD HH:mm:ss" format
            orderNumber: orderNumber,
            currencyFactor: 1,
            itemRounding: { decimals: 2, interval: 0.01, roundForNet: true },
            totalRounding: { decimals: 2, interval: 0.01, roundForNet: true },
            affiliateCode: "BackendCsvOrderImport",
            source: "backend",
            orderCustomer: this.buildOrderCustomer(customer),
            addresses: [this.buildOrderAddress(billingAddress, billingAddressId, customer)],
            price: {
                netPrice: netPrice.toFixed(2),
                totalPrice: isTaxFree ? netPrice.toFixed(2) : grossPrice.toFixed(2),
                calculatedTaxes: isTaxFree ? [] : [{
                    extensions: [],
                    tax: totalTaxPrice.toFixed(2),
                    taxRate: taxRate,
                    price: (isGross ? grossPrice : netPrice).toFixed(2)
                }],
                taxRules: isTaxFree ? [] : [{
                    extensions: [],
                    taxRate: taxRate,
                    percentage: 100
                }],
                positionPrice: (isGross ? grossPrice : netPrice).toFixed(2),
                rawTotal: isTaxFree ? netPrice.toFixed(2) : grossPrice.toFixed(2),
                taxStatus: taxStatus
            },
            shippingCosts: this.buildShippingCosts(taxRate, isTaxFree),
            lineItems: lineItems,
            deliveries: [this.buildDelivery(shippingAddress, shippingConfig, lineItems, deliveryDate, taxRate, isTaxFree, customer)],
            transactions: [this.buildTransaction(profile.defaultValues?.paymentMethodId, taxRate, isTaxFree)],
            tagIds: [
                "018f060dc2f278a0a825c9ce447b10cc" // Standard tag
            ]
        };
        
        if (DEBUG_API) {
            console.log('[Order Builder] Final order payload:', JSON.stringify(orderPayload, null, 2));
            console.log('[Order Builder] Line items count:', lineItems.length);
            console.log('[Order Builder] Addresses count:', orderPayload.addresses.length);
            console.log('[Order Builder] Deliveries count:', orderPayload.deliveries.length);
            console.log('[Order Builder] Critical ID validation:');
            console.log('- billingAddressId:', orderPayload.billingAddressId, '(type:', typeof orderPayload.billingAddressId, ')');
            console.log('- addresses[0].id:', orderPayload.addresses[0]?.id, '(type:', typeof orderPayload.addresses[0]?.id, ')');
            console.log('- currencyId:', orderPayload.currencyId, '(type:', typeof orderPayload.currencyId, ')');
            console.log('- languageId:', orderPayload.languageId, '(type:', typeof orderPayload.languageId, ')');
            console.log('- salesChannelId:', orderPayload.salesChannelId, '(type:', typeof orderPayload.salesChannelId, ')');
            console.log('- Payload validation:');
            console.log('- orderPayload is object:', typeof orderPayload === 'object');
            console.log('- orderPayload is array:', Array.isArray(orderPayload));
            console.log('- orderPayload keys:', Object.keys(orderPayload));
            console.log('- lineItems is array:', Array.isArray(orderPayload.lineItems));
            console.log('- addresses is array:', Array.isArray(orderPayload.addresses));
            console.log('- deliveries is array:', Array.isArray(orderPayload.deliveries));
            console.log('- transactions is array:', Array.isArray(orderPayload.transactions));
            console.log('- tags is array:', Array.isArray(orderPayload.tags));
            console.log('- tagIds is array:', Array.isArray(orderPayload.tagIds));
            console.log('- customFields is object:', typeof orderPayload.customFields === 'object');
            console.log('- customFields is not array:', !Array.isArray(orderPayload.customFields));
            console.log('- Price validation:');
            console.log('  - price.totalPrice is number:', typeof orderPayload.price.totalPrice === 'number');
            console.log('  - price.netPrice is number:', typeof orderPayload.price.netPrice === 'number');
            console.log('  - shippingCosts.totalPrice is number:', typeof orderPayload.shippingCosts.totalPrice === 'number');
            if (orderPayload.lineItems.length > 0) {
                console.log('  - lineItem[0].price.totalPrice is number:', typeof orderPayload.lineItems[0].price.totalPrice === 'number');
                console.log('  - lineItem[0].priceDefinition.price is number:', typeof orderPayload.lineItems[0].priceDefinition.price === 'number');
            }
        }
        
        // Add fallback info to orderPayload for UI display
        orderPayload.fallbackPriceProducts = fallbackPriceProducts;
        
        return orderPayload;
    }

    /**
     * Build line items from CSV data
     * @param {Object} csvData - CSV data
     * @param {Object} profile - Profile configuration
     * @param {Object} customer - Customer object
     * @returns {Object} Line items and total price
     */
    async buildLineItems(csvData, profile, customer, taxRate = 19, taxStatus = 'net') {
        const lineItems = [];
        let totalPrice = 0;
        const fallbackPriceProducts = []; // Track products using fallback pricing
        
        // Collect all product numbers for bulk lookup
        const productNumbers = csvData.data.map(row => {
            const productMappingKey = Object.keys(profile.fieldMapping || {}).find(key => 
                profile.fieldMapping[key].includes('productNumber') || 
                key.toLowerCase().includes('product') ||
                key.toLowerCase().includes('artikel')
            );
            return productMappingKey ? row[productMappingKey] : null;
        }).filter(Boolean);
        
        // Get product data and prices
        const products = await this.getProductsByNumbers(productNumbers);
        const productPricesResult = this.priceCalculator ? 
            await this.priceCalculator.getProductPricesForProfile(productNumbers, profile, customer) : 
            { success: true, prices: {}, validation: { missingProducts: [] } };
        
        // IMPROVEMENT: Handle strict validation errors
        if (productPricesResult.success === false && productPricesResult.error === 'MISSING_PRODUCTS') {
            console.error('[OrderBuilder] 🚫 Product validation failed:', productPricesResult.message);
            throw new Error(`Import aborted due to missing products: ${productPricesResult.missingProducts.join(', ')}`);
        }
        
        // Extract price data from improved response format
        const productPrices = productPricesResult.prices || productPricesResult;
        const validationInfo = productPricesResult.validation || {};
        
        // IMPROVEMENT: Log validation warnings
        if (productPricesResult.warnings && productPricesResult.warnings.length > 0) {
            console.warn('[OrderBuilder] ⚠️ Product validation warnings:', productPricesResult.warnings);
        }
        
        // Build line items
        for (let index = 0; index < csvData.data.length; index++) {
            const row = csvData.data[index];
            const lineItemResult = await this.buildLineItem(row, index, profile, products, productPrices, customer, taxRate, taxStatus);
            const lineItem = lineItemResult?.lineItem;
            
            if (lineItem) {
                lineItems.push(lineItem);
                
                // Track fallback pricing products
                if (lineItemResult?.usedFallbackPrice) {
                    fallbackPriceProducts.push({
                        productId: lineItem.productId,
                        productNumber: lineItemResult.productNumber,
                        productName: lineItem.label
                    });
                }
                
                // Add to total price if pricing is enabled
                if (profile.defaultValues?.withPrices && lineItem.price?.unitPrice) {
                    totalPrice += parseFloat((lineItem.price.unitPrice * lineItem.quantity).toFixed(2));
                }
            }
        }
        
        // IMPROVEMENT: Enhanced return with validation info
        return { 
            lineItems, 
            totalPrice, 
            fallbackPriceProducts,
            validation: {
                ...validationInfo,
                fallbackPricesUsed: fallbackPriceProducts.length,
                strictValidation: profile.defaultValues?.strictProductValidation || false
            }
        };
    }

    /**
     * Build individual line item
     * @param {Object} row - CSV row data
     * @param {number} index - Row index
     * @param {Object} profile - Profile configuration
     * @param {Array} products - Product lookup data
     * @param {Object} productPrices - Price lookup data
     * @returns {Object} Line item object
     */
    async buildLineItem(row, index, profile, products, productPrices, customer = null, taxRate = 19, taxStatus = 'net') {
        // Extract product data from row
        const productData = this.extractProductDataFromRow(row, profile);
        if (!productData.productNumber) return null;

        // Find product in lookup data
        const product = products.find(p => p.productNumber === productData.productNumber);

        if (product) {
            // Real product found
            const priceInfo = productPrices[product.productNumber] || {};
            // Use gross price for B2C (taxStatus=gross), net price otherwise
            const isGross = taxStatus === 'gross';
            const unitPrice = profile.defaultValues?.withPrices
                ? (isGross ? (priceInfo.gross || priceInfo.unitPrice || 0) : (priceInfo.unitPrice || 0))
                : 0;

            // Check if this is using fallback pricing (system fallback rule)
            const usedFallbackPrice = profile.defaultValues?.withPrices &&
                priceInfo.source === 'system-fallback-shopkunden';

            // Get tax-free status from profile and customer
            const isTaxFreeForLineItem = this.calculateTaxFreeStatus(customer, profile);
            
            const lineItemPrice = this.priceCalculator ? 
                this.priceCalculator.calculateLineItemPrice({
                    unitPrice: unitPrice,
                    quantity: productData.quantity,
                    taxRate: taxRate,
                    isTaxFree: isTaxFreeForLineItem,
                    withPrices: profile.defaultValues?.withPrices
                }) : 
                this.calculateBasicLineItemPrice(unitPrice, productData.quantity, 19, isTaxFreeForLineItem);
            
            return {
                lineItem: {
                    productId: product.id,
                    referencedId: product.id,
                    payload: {
                        productNumber: product.productNumber,
                        taxId: "018ad12f18c072b09b5565d801f600bf",
                        optionIds: [],
                        options: []
                    },
                    identifier: product.id,
                    type: "product",
                    label: product.name || productData.productName || product.productNumber,
                    quantity: productData.quantity,
                    position: index + 1,
                    price: {
                        unitPrice: lineItemPrice.unitPrice,
                        totalPrice: lineItemPrice.totalPrice,
                        quantity: productData.quantity,
                        calculatedTaxes: lineItemPrice.calculatedTaxes,
                        taxRules: lineItemPrice.taxRules,
                        referencePrice: null,
                        listPrice: null,
                        regulationPrice: null
                    },
                    priceDefinition: {
                        type: "quantity",
                        price: lineItemPrice.unitPrice,
                        quantity: productData.quantity,
                        taxRules: lineItemPrice.taxRules,
                        listPrice: null,
                        isCalculated: profile.defaultValues?.withPrices ? true : false,
                        referencePriceDefinition: null
                    }
                },
                usedFallbackPrice: usedFallbackPrice,
                productNumber: product.productNumber
            };
        } else {
            // Product not found - create placeholder
            const lineItemId = this.generateUUID().replace(/-/g, '');
            return {
                lineItem: {
                    productId: null,
                referencedId: null,
                payload: {
                    productNumber: productData.productNumber,
                    taxId: "018ad12f18c072b09b5565d801f600bf",
                    optionIds: [],
                    options: []
                },
                identifier: lineItemId,
                type: "product",
                label: `Product not found: ${productData.productNumber}`,
                quantity: productData.quantity,
                position: index + 1,
                price: {
                    unitPrice: 0,
                    totalPrice: 0,
                    quantity: productData.quantity,
                    calculatedTaxes: [{
                        extensions: [],
                        tax: 0,
                        taxRate: taxRate,
                        price: 0
                    }],
                    taxRules: [{
                        extensions: [],
                        taxRate: taxRate,
                        percentage: 100
                    }],
                    referencePrice: null,
                    listPrice: null,
                    regulationPrice: null
                },
                priceDefinition: {
                    type: "quantity",
                    price: 0,
                    quantity: productData.quantity,
                    taxRules: [{
                        extensions: [],
                        taxRate: taxRate,
                        percentage: 100
                    }],
                    listPrice: null,
                    isCalculated: false,
                    referencePriceDefinition: null
                    }
                },
                usedFallbackPrice: false,
                productNumber: productData.productNumber
            };
        }
    }

    /**
     * Extract product data from CSV row
     * @param {Object} row - CSV row
     * @param {Object} profile - Profile configuration
     * @returns {Object} Product data
     */
    extractProductDataFromRow(row, profile) {
        const fieldMapping = profile.fieldMapping || {};
        
        // Find product number mapping
        const productMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('productNumber') || 
            key.toLowerCase().includes('product') ||
            key.toLowerCase().includes('artikel')
        );
        
        // Find quantity mapping
        const quantityMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('quantity') || 
            key.toLowerCase().includes('quantity') ||
            key.toLowerCase().includes('menge') ||
            key.toLowerCase().includes('anzahl')
        );
        
        // Find product name mapping
        const nameMappingKey = Object.keys(fieldMapping).find(key => 
            fieldMapping[key].includes('name') || 
            key.toLowerCase().includes('name') ||
            key.toLowerCase().includes('bezeichnung')
        );
        
        return {
            productNumber: productMappingKey ? row[productMappingKey] : null,
            quantity: quantityMappingKey ? parseInt(row[quantityMappingKey]) || 1 : 1,
            productName: nameMappingKey ? row[nameMappingKey] : null
        };
    }

    /**
     * Build order customer object
     * @param {Object} customer - Customer data
     * @returns {Object} Order customer object
     */
    buildOrderCustomer(customer) {
        return {
            customerId: customer.id,
            customerNumber: customer.customerNumber,
            email: customer.email,
            firstName: customer.firstName,
            lastName: customer.lastName,
            salutationId: customer.salutationId,
            title: customer.title || null,
            vatIds: customer.vatIds || [],
            company: customer.company || ""
        };
    }

    /**
     * Build order address object
     * @param {Object} address - Address data
     * @param {string} addressId - Address ID
     * @param {Object} customer - Customer data
     * @returns {Object} Order address object
     */
    buildOrderAddress(address, addressId, customer) {
        return {
            id: addressId,
            salutationId: address.salutationId || customer.salutationId,
            company: address.company || customer.company || null, // null instead of empty string
            department: address.department || null,
            firstName: address.firstName || customer.firstName,
            lastName: address.lastName || customer.lastName,
            additionalAddressLine1: address.additionalAddressLine1 || null,
            additionalAddressLine2: address.additionalAddressLine2 || null,
            street: address.street,
            zipcode: address.zipcode,
            city: address.city,
            countryId: address.countryId,
            vatId: customer.vatIds && customer.vatIds.length > 0 ? customer.vatIds[0] : null,
            phoneNumber: address.phoneNumber || customer.phoneNumber || null
        };
    }

    /**
     * Build delivery address object (without ID)
     * @param {Object} address - Address data
     * @returns {Object} Delivery address object
     */
    buildDeliveryAddress(address) {
        return {
            salutationId: address.salutationId,
            company: address.company || "",
            department: address.department || null,
            firstName: address.firstName,
            lastName: address.lastName,
            additionalAddressLine1: address.additionalAddressLine1 || null,
            additionalAddressLine2: address.additionalAddressLine2 || null,
            street: address.street,
            zipcode: address.zipcode,
            city: address.city,
            countryId: address.countryId,
            phoneNumber: address.phoneNumber || ""
        };
    }

    /**
     * Build shipping costs object
     * @param {number} taxRate - Tax rate
     * @param {boolean} isTaxFree - Tax free status
     * @returns {Object} Shipping costs object
     */
    buildShippingCosts(taxRate, isTaxFree) {
        return {
            unitPrice: 0,
            totalPrice: 0,
            quantity: 1,
            calculatedTaxes: isTaxFree ? [] : [{
                extensions: [],
                tax: 0,
                taxRate: taxRate,
                price: 0
            }],
            taxRules: isTaxFree ? [] : [{
                extensions: [],
                taxRate: taxRate,
                percentage: 100
            }],
            referencePrice: null,
            listPrice: null,
            regulationPrice: null
        };
    }

    /**
     * Build delivery object
     * @param {Object} shippingAddress - Shipping address
     * @param {Object} shippingConfig - Shipping configuration
     * @param {Array} lineItems - Line items
     * @param {string} deliveryDate - Delivery date
     * @returns {Object} Delivery object
     */
    buildDelivery(shippingAddress, shippingConfig, lineItems, deliveryDate, taxRate, isTaxFree, customer = null) {
        return {
            stateId: "018ad12f18f47001871826d7835c2266",
            shippingMethodId: shippingConfig.shippingMethodId,
            shippingOrderAddress: {
                salutationId: shippingAddress.salutationId || customer?.salutationId,
                company: shippingAddress.company || null,
                department: shippingAddress.department || null,
                firstName: shippingAddress.firstName || customer?.firstName,
                lastName: shippingAddress.lastName || customer?.lastName,
                additionalAddressLine1: shippingAddress.additionalAddressLine1 || null,
                additionalAddressLine2: shippingAddress.additionalAddressLine2 || null,
                street: shippingAddress.street,
                zipcode: shippingAddress.zipcode,
                city: shippingAddress.city,
                countryId: shippingAddress.countryId,
                phoneNumber: shippingAddress.phoneNumber || ""
            },
            shippingDateEarliest: deliveryDate,
            shippingDateLatest: deliveryDate,
            shippingCosts: {
                unitPrice: 0,
                totalPrice: 0,
                quantity: 1,
                calculatedTaxes: isTaxFree ? [] : [{
                    extensions: [],
                    tax: 0,
                    taxRate: taxRate,
                    price: 0
                }],
                taxRules: isTaxFree ? [] : [{
                    extensions: [],
                    taxRate: taxRate,
                    percentage: 100
                }]
            }
        };
    }

    /**
     * Build transaction object
     * @param {string} paymentMethodId - Payment method ID
     * @param {number} taxRate - Tax rate
     * @param {boolean} isTaxFree - Tax free status
     * @returns {Object} Transaction object
     */
    buildTransaction(paymentMethodId, taxRate, isTaxFree) {
        return {
            paymentMethodId: paymentMethodId,
            amount: {
                unitPrice: 0,
                totalPrice: 0,
                quantity: 0,
                calculatedTaxes: [{
                    tax: 0,
                    taxRate: taxRate,
                    price: 0
                }],
                taxRules: [{
                    taxRate: taxRate,
                    percentage: 100
                }]
            },
            stateId: "018ad12f18f871b594ec3dfc90cde76c" // Standard transaction state
        };
    }

    /**
     * Build custom fields object
     * @param {string} orderNumber - Order number
     * @param {string} deliveryDate - Delivery date
     * @returns {Object} Custom fields object
     */
    buildCustomFields(orderNumber, deliveryDate) {
        const formattedOrderNumber = orderNumber.replace('-', '/');
        return {
            shm_order_set_delivery_date: deliveryDate.split('T')[0], // Format as YYYY-MM-DD
            shm_order_set_order_number: formattedOrderNumber,
            shm_order_set_hide_delivery_address: true,
            shm_order_set_show_company_name: false,
            shm_order_set_shop_email: true,
            shm_order_set_babyone_no: ""
        };
    }

    /**
     * Get delivery date from CSV or default
     * @param {Object} csvData - CSV data
     * @param {Object} profile - Profile configuration
     * @returns {string} Delivery date
     */
    getDeliveryDate(csvData, profile) {
        if (csvData.data && csvData.data.length > 0) {
            const deliveryMappingKey = Object.keys(profile.fieldMapping || {}).find(key => 
                profile.fieldMapping[key].includes('deliveries.shippingDateEarliest') || 
                key.toLowerCase().includes('delivery')
            );
            if (deliveryMappingKey && csvData.data[0][deliveryMappingKey]) {
                return csvData.data[0][deliveryMappingKey];
            }
        }
        
        // Default to 3 days from now
        return new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    /**
     * Calculate tax-free status for customer
     * @param {Object} customer - Customer data
     * @param {Object} profile - Profile configuration
     * @returns {boolean} Tax-free status
     */
    calculateTaxFreeStatus(customer, profile) {
        return this.determineTaxStatus(customer, profile) === 'tax-free';
    }

    /**
     * Determine the tax status for an order: "tax-free", "net", or "gross"
     * @param {Object} customer - Customer data
     * @param {Object} profile - Profile configuration
     * @returns {string} "tax-free" | "net" | "gross"
     */
    determineTaxStatus(customer, profile) {
        // PRIORITY 1: Profile override → tax-free
        if (profile?.defaultValues?.forceTaxFree === true) {
            return 'tax-free';
        }

        // PRIORITY 2: EU B2B with non-DE VAT ID → tax-free (reverse charge)
        if (customer?.vatIds?.length > 0 &&
            customer.vatIds[0] !== "" &&
            !customer.vatIds[0].includes("DE")) {
            return 'tax-free';
        }

        // PRIORITY 3: B2C without company/VAT → gross (end consumer)
        const hasCompany = customer?.company && customer.company.trim() !== '';
        const hasVatId = customer?.vatIds?.length > 0 && customer.vatIds[0] !== '';
        if (!hasCompany && !hasVatId) {
            return 'gross';
        }

        // Default: B2B DE or with company → net
        return 'net';
    }

    /**
     * Generate UUID
     * @returns {string} UUID string
     */
    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Basic order price calculation fallback
     * @param {number} totalPrice - Net total price
     * @param {number} taxRate - Tax rate percentage
     * @param {boolean} isTaxFree - Whether order is tax-free
     * @returns {Object} Order price structure
     */
    calculateBasicOrderPrice(totalPrice, taxRate, isTaxFree) {
        const netPrice = parseFloat(totalPrice) || 0;
        const taxAmount = isTaxFree ? 0 : (netPrice * (taxRate / 100));
        const grossPrice = netPrice + taxAmount;
        
        return {
            netPrice: parseFloat(netPrice.toFixed(2)),
            totalPrice: parseFloat(grossPrice.toFixed(2)),
            positionPrice: parseFloat(netPrice.toFixed(2)),
            rawTotal: parseFloat(grossPrice.toFixed(2)),
            taxStatus: isTaxFree ? "tax-free" : "net",
            calculatedTaxes: isTaxFree ? [] : [{
                tax: parseFloat(taxAmount.toFixed(2)),
                taxRate: taxRate,
                price: parseFloat(netPrice.toFixed(2))
            }],
            taxRules: isTaxFree ? [] : [{
                taxRate: taxRate,
                percentage: 100.0
            }]
        };
    }

    /**
     * Basic line item price calculation fallback
     * @param {number} unitPrice - Unit price
     * @param {number} quantity - Quantity
     * @param {number} taxRate - Tax rate percentage
     * @param {boolean} isTaxFree - Whether item is tax-free
     * @returns {Object} Line item price structure
     */
    calculateBasicLineItemPrice(unitPrice, quantity, taxRate, isTaxFree) {
        const netUnitPrice = parseFloat(unitPrice) || 0;
        const qty = parseInt(quantity) || 1;
        const netTotalPrice = netUnitPrice * qty;
        const taxAmount = isTaxFree ? 0 : (netTotalPrice * (taxRate / 100));
        
        return {
            unitPrice: parseFloat(netUnitPrice.toFixed(2)),
            totalPrice: parseFloat(netTotalPrice.toFixed(2)), // For tax-free: same as net price
            quantity: qty,
            calculatedTaxes: isTaxFree ? [] : [{
                extensions: [],
                tax: parseFloat(taxAmount.toFixed(2)),
                taxRate: taxRate,
                price: parseFloat(netTotalPrice.toFixed(2))
            }],
            taxRules: isTaxFree ? [] : [{
                extensions: [],
                taxRate: taxRate,
                percentage: 100
            }],
            referencePrice: null,
            listPrice: null,
            regulationPrice: null
        };
    }

    // Implemented methods using proper API calls
    async getProductsByNumbers(productNumbers) {
        if (!productNumbers || productNumbers.length === 0) return [];
        
        try {
            return await this.baseClient.findProductsByNumbers(productNumbers);
        } catch (error) {
            console.error('[Order Builder] Error fetching products:', error);
            return [];
        }
    }

    async getShippingConfiguration(shippingAddress, profile) {
        if (this.countryShipping) {
            return await this.countryShipping.getShippingConfiguration(shippingAddress, profile);
        }
        
        // Fallback if countryShipping not available
        return {
            shippingMethodId: profile.defaultValues?.shippingMethodId || '018ad1acd4fa726bb25aa7e7aad99814'
        };
    }

    async getTaxShippingConfig(country, profile) {
        if (this.countryShipping) {
            return await this.countryShipping.getCountryTaxShipping(country, profile);
        }
        
        // Fallback if countryShipping not available
        return {
            taxRate: 19,
            taxId: '018ad12f18c072b09b5565d801f600bf',
            shippingMethodId: profile.defaultValues?.shippingMethodId || '018ad1acd4fa726bb25aa7e7aad99814'
        };
    }
}

export default OrderBuilder;